<template>
  <div>
    <v-autocomplete
      v-if="numberInput"
      @input="$emit('input', $event)"
      :value="value"
      :items="items"
      :rules="rules"
      :loading="loading"
      :filter="filterObject"
      :id="id"
      :label="label"
      :item-text="itemText"
      :disabled="isReadOnly || disabled"
      :multiple="!!multiple"
      :no-filter="noFilter"
    >
      <!--  This template binds to the item slot of the v-autocomplete component
            With v-bind:item="{item}" we bind the variable item used in the v-autocomplete slot
            For more documentation about the "item" variable loot at v-autocomplete API-docs
            https://vuetifyjs.com/en/api/v-autocomplete/#slots and look up vue slots docs
            https://v2.vuejs.org/v2/guide/components-slots.html#Scoped-Slots
     -->
      <template v-if="hasItemsSlot()" v-slot:item="{ item }">
        <!--  The parent component has the ability to modify the slot of an v-autocomplete component
              We created a replica of the v-autocomplete v-bind:item slot so we can inject it inside
              a v-slot:item template. So it can refer to the v-slot:item inside the v-autocomplete component.
              See https://v2.vuejs.org/v2/guide/components-slots.html#Scoped-Slots for more docs about scopes slots
        -->
        <slot v-if="hasItemsSlot()" name="item" v-bind:item="{ item }"> </slot>
      </template>
      <!--  This template binds to the selection slot of the v-autocomplete component
            With v-bind:selection="{item}" we bind the variable item used in the v-autocomplete slot
            For more documentation about the "selection" variable loot at v-autocomplete API-docs
            https://vuetifyjs.com/en/api/v-autocomplete/#slots and look up vue slots docs
            https://v2.vuejs.org/v2/guide/components-slots.html#Scoped-Slots
      -->
      <template v-if="hasSelectionSlot()" v-slot:selection="{ item }">
        <!--  The parent component has the ability to modify the slot of an v-autocomplete component
              We created a replica of the v-autocomplete v-bind:selection slot so we can inject it inside
              a v-slot:selection template. So it can refer to the v-slot:item inside the v-autocomplete component.
              See https://v2.vuejs.org/v2/guide/components-slots.html#Scoped-Slots for more docs about scopes slots
        -->

        <slot v-if="hasSelectionSlot()" name="selection" v-bind:selection="{ item }"></slot>
      </template>
    </v-autocomplete>
    <v-autocomplete
      v-else
      @input="$emit('input', $event)"
      :value="value"
      :items="items"
      :rules="rules"
      :loading="loading"
      :filter="filterObject"
      :id="id"
      :label="label"
      :disabled="isReadOnly || disabled"
      :search-input.sync="input"
      :item-text="itemText"
      :multiple="!!multiple"
      :no-filter="noFilter"
      :no-data-text="$t(noDataTextKey)"
      :hide-no-data="hideNoDataIfNoInput ? !input || input.length < 2 || loading : false"
    >
      <!--  This template binds to the item slot of the v-autocomplete component
            With v-bind:item="{item}" we bind the variable item used in the v-autocomplete slot
            For more documentation about the "item" variable loot at v-autocomplete API-docs
            https://vuetifyjs.com/en/api/v-autocomplete/#slots and look up vue slots docs
            https://v2.vuejs.org/v2/guide/components-slots.html#Scoped-Slots
      -->
      <template v-if="hasItemsSlot()" v-slot:item="{ item }">
        <!--  The parent component has the ability to modify the slot of an v-autocomplete component
              We created a replica of the v-autocomplete v-bind:item slot so we can inject it inside
              a v-slot:item template. So it can refer to the v-slot:item inside the v-autocomplete component.
              See https://v2.vuejs.org/v2/guide/components-slots.html#Scoped-Slots for more docs about scopes slots
        -->
        <slot v-if="hasItemsSlot()" name="item" v-bind:item="{ item }"> </slot>
      </template>
      <!--  This template binds to the selection slot of the v-autocomplete component
            With v-bind:selection="{item}" we bind the variable item used in the v-autocomplete slot
            For more documentation about the "selection" variable loot at v-autocomplete API-docs
            https://vuetifyjs.com/en/api/v-autocomplete/#slots and look up vue slots docs
            https://v2.vuejs.org/v2/guide/components-slots.html#Scoped-Slots
      -->
      <template v-if="hasSelectionSlot()" v-slot:selection="{ item }">
        <!--  The parent component has the ability to modify the slot of an v-autocomplete component
              We created a replica of the v-autocomplete v-bind:selection slot so we can inject it inside
              a v-slot:selection template. So it can refer to the v-slot:item inside the v-autocomplete component.
              See https://v2.vuejs.org/v2/guide/components-slots.html#Scoped-Slots for more docs about scopes slots
        -->

        <slot v-if="hasSelectionSlot()" name="selection" v-bind:selection="{ item }"></slot>
      </template>
    </v-autocomplete>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import ObjectInputMixin from '@/mixins/inputMixins/subMixins/ObjectInputMixin';
import { LocaleMessage } from 'vue-i18n';

@Component
export default class AutocompleteInput extends mixins(ObjectInputMixin) {
  @Prop(String) id: string | undefined;
  @Prop(String) label: string | undefined;
  @Prop() readonly value: string | number | object | undefined;
  @Prop(String) search: string | undefined;
  @Prop(String) itemText: string | undefined;
  @Prop({ default: 'algemeen.geen-data' }) noDataTextKey: string | undefined;

  @Prop(Boolean) readonly loading!: boolean;
  @Prop(Boolean) readonly disabled!: boolean;
  @Prop(Boolean) readonly numberInput!: boolean;
  @Prop(Boolean) readonly multiple!: boolean;
  @Prop({ default: false }) hasChild: boolean;

  @Prop({ default: false }) readonly noFilter: boolean | undefined;
  @Prop({ default: false }) readonly hideNoDataIfNoInput: boolean | undefined;

  @Prop({ default: false }) withSubTitles!: boolean;

  @Prop(Array) readonly items!: [];
  @Prop({ default: Array }) readonly rules!: ((v: string) => string | boolean | LocaleMessage)[];
  @Prop() filterObject!: Function;
  input = '';

  @Watch('input')
  onChildChanged(search: string) {
    if (this.input) {
      this.$emit('search', search);
    }
  }

  hasItemsSlot() {
    return !!this.$scopedSlots.item;
  }

  hasSelectionSlot() {
    return !!this.$scopedSlots.selection;
  }
}
</script>
